import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Browser } from '@capacitor/browser';

/**
 * Check if the app is running as a native app (iOS/Android)
 */
export const isNative = Capacitor.isNativePlatform();

/**
 * Initialize native app UI settings
 * - Adds 'native-app' and platform classes to body
 * - Configures status bar for edge-to-edge display
 */
export async function initNativeUI(): Promise<void> {
  if (!isNative) return;

  // Add native-app class to body for CSS targeting
  document.body.classList.add('native-app');

  // Add platform-specific class
  const platform = Capacitor.getPlatform();
  document.body.classList.add(`platform-${platform}`);

  // Configure status bar for edge-to-edge (transparent overlay)
  // Style.Dark = light/white icons (for dark backgrounds)
  try {
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setOverlaysWebView({ overlay: true });
  } catch (error) {
    console.warn('StatusBar configuration failed:', error);
  }

  // Track keyboard height via visualViewport (consumed by bottom-sheets).
  if (window.visualViewport) {
    const updateKeyboardHeight = () => {
      const vv = window.visualViewport!;
      const keyboardHeight = window.innerHeight - vv.height;
      document.documentElement.style.setProperty(
        '--keyboard-height',
        `${Math.max(0, keyboardHeight)}px`
      );
    };
    window.visualViewport.addEventListener('resize', updateKeyboardHeight);
    window.visualViewport.addEventListener('scroll', updateKeyboardHeight);
  }
}

/**
 * Check if running on iOS
 */
export const isIOS = Capacitor.getPlatform() === 'ios';

/**
 * Check if running on Android
 */
export const isAndroid = Capacitor.getPlatform() === 'android';

/**
 * Check if running in web browser
 */
export const isWeb = Capacitor.getPlatform() === 'web';

/**
 * Production app URL - used for sharing links from native apps
 */
export const APP_BASE_URL = 'https://app.parentpath.de';

/**
 * Get the base URL for shareable links
 * - Native apps: Always use production URL (app.parentpath.de)
 * - Web: Use current origin (supports dev/staging environments)
 */
export function getAppBaseUrl(): string {
  if (isNative) {
    return APP_BASE_URL;
  }
  return window.location.origin;
}

/**
 * Get the appropriate redirect URL for auth callbacks.
 *
 * Always returns an https URL so Universal Links (iOS) and App Links (Android)
 * can intercept the auth redirect. The custom `parentpath://` scheme is kept
 * only as an in-app fallback handler (Info.plist / AndroidManifest), not as
 * an auth target.
 */
export function getAuthRedirectUrl(path: string = '/auth/callback'): string {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  if (isNative) {
    return `${APP_BASE_URL}${cleanPath}`;
  }
  return `${window.location.origin}${cleanPath}`;
}

/**
 * Parse a deep link URL and extract auth tokens
 * Supports both hash fragments (#access_token=...) and query params (?access_token=...)
 */
export function parseDeepLinkAuth(url: string): {
  accessToken: string | null;
  refreshToken: string | null;
  type: string | null;
  error: string | null;
  errorDescription: string | null;
} {
  try {
    const urlObj = new URL(url);

    // Check hash fragment first (Supabase typically uses this)
    let params = new URLSearchParams(urlObj.hash.substring(1));

    // Fallback to query params if no hash
    if (!params.get('access_token') && urlObj.search) {
      params = new URLSearchParams(urlObj.search);
    }

    return {
      accessToken: params.get('access_token'),
      refreshToken: params.get('refresh_token'),
      type: params.get('type'),
      error: params.get('error'),
      errorDescription: params.get('error_description'),
    };
  } catch {
    console.error('Failed to parse deep link URL:', url);
    return {
      accessToken: null,
      refreshToken: null,
      type: null,
      error: 'invalid_url',
      errorDescription: 'Failed to parse deep link URL',
    };
  }
}

/**
 * Extract the path from a deep link URL
 * e.g., "parentpath://auth/callback" -> "/auth/callback"
 */
export function getDeepLinkPath(url: string): string {
  try {
    const urlObj = new URL(url);
    // For custom schemes, the host is part of the path
    // parentpath://auth/callback -> host="auth", pathname="/callback"
    const path = urlObj.host + urlObj.pathname;
    return '/' + path.replace(/^\/+/, '');
  } catch {
    return '/';
  }
}

/**
 * Open an external URL
 * - Native: Opens in in-app browser with Safari/Chrome view controller
 * - Web: Opens in new tab
 */
export async function openExternalUrl(url: string): Promise<void> {
  if (isNative) {
    await Browser.open({ url });
  } else {
    window.open(url, '_blank');
  }
}
